import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Menu,
  DialogContent,
  SxProps,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  createTag,
  getAllTags,
  removeTag,
  updateTag,
} from '../api/requests/events';
import { useAlert } from '../context/AlertContext';
import { colorOptions, colors } from '../utils/constants';
import CommonDialog from '../components/common/CommonDialog';
import { menuItemStyles } from './Groups';

interface TagsType {
  eventTagId: number;
  tagName: string;
  description: null | string;
  colour: string;
  usageCount: number;
}

interface CreateTagPanelProps {
  setMode: (mode: string) => void;
  fetchEvents: () => Promise<void>;
  RefreshTags: () => Promise<void>;
}

interface FormErrors {
  tagName: string;
}

export default function CreateTagPanel({
  setMode,
  fetchEvents,
  RefreshTags,
}: CreateTagPanelProps) {
  const { showAlert } = useAlert();
  const [tagName, setTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [tags, setTags] = useState<TagsType[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [selectedTag, setSelectedTag] = useState<TagsType | null>(null);
  const [errors, setErrors] = useState<FormErrors>({
    tagName: '',
  });
  const isEditing = editIndex !== null;

  const closeDialog = () => {
    setTagName('');
    setSelectedColor('');
    setEditIndex(null);
    setSelectedTag(null);
    setMode('');
  };

  const validateTagName = (name: string) => {
    if (name.trim() === '') {
      setErrors((prev) => ({ ...prev, tagName: 'Tag name is required' }));
      return false;
    }
    setErrors((prev) => ({ ...prev, tagName: '' }));
    return true;
  };

  const handleCreateOrUpdateTag = async () => {
    if (!validateTagName(tagName)) {
      showAlert('Please fill all fields', 'error');
      return;
    }
    try {
      const response = await (isEditing
        ? updateTag({
            eventTagId: editIndex!,
            tagName: tagName.trim(),
            description: '',
            colour: selectedColor,
          })
        : createTag({
            tagName: tagName.trim(),
            description: '',
            colour: selectedColor,
          }));
      if (response.status === 201 || response.status === 200) {
        if (isEditing) await fetchTags();
        await fetchEvents();
        showAlert(
          isEditing ? 'Tag updated successfully.' : 'Tag created successfully.',
          'success'
        );
        if (!isEditing) {
          closeDialog();
        } else {
          setTagName('');
          setSelectedColor('');
          setEditIndex(null);
        }
        await RefreshTags();
      } else {
        showAlert(
          isEditing ? 'Tag update failed.' : 'Tag creation failed.',
          'error'
        );
      }
    } catch (error) {
      console.error('Error creating/updating tag:', error);
      showAlert('Error processing tag.', 'error');
    }
  };

  const handleEditTag = () => {
    if (selectedTag) {
      setTagName(selectedTag.tagName);
      setSelectedColor(selectedTag.colour);
      setEditIndex(selectedTag.eventTagId);
    }
    handleMenuClose();
  };

  const handleDeleteTag = async () => {
    if (!selectedTag) return;
    try {
      const response = await removeTag(selectedTag.eventTagId);
      if (response && response.status === 200) {
        setSelectedTag(null);
        await fetchTags();
        await fetchEvents();
        showAlert('Tag deleted successfully', 'success');
      } else {
        showAlert('Failed to delete tag', 'error');
      }
    } catch (error) {
      console.error('Error deleting tag:', error);
      showAlert('Error deleting tag', 'error');
    }
    handleMenuClose();
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedTag(tags[index]);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTag(null);
  };

  const handleTagName = (value: string) => {
    setTagName(value);
    setErrors((prev) => ({ ...prev, tagName: '' }));
  };

  const fetchTags = async () => {
    try {
      const response = await getAllTags();
      if (response.status === 200) {
        setTags(response.data);
      } else {
        showAlert('Failed to fetch tags.', 'error');
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
      showAlert('Error fetching tags.', 'error');
    }
  };

  const getTagColour = (tag: string) => {
    const colorObj = colorOptions.find((c) => c.value === tag);
    return colorObj ? colorObj.chipColor : '#9e9e9e';
  };

  useEffect(() => {
    fetchTags();
  }, []);

  return (
    <CommonDialog
      title={isEditing ? 'Edit Tag' : 'Create New Tag'}
      hideActionButtons
      cancelButton={closeDialog}
    >
      <DialogContent sx={{ minWidth: 550 }}>
        <InputLabel
          sx={{ fontSize: '14px', fontFamily: 400, color: colors.black }}
        >
          Tag Name <span style={{ color: 'red' }}>*</span>
        </InputLabel>
        <TextField
          placeholder='e.g., Important'
          fullWidth
          variant='outlined'
          value={tagName}
          onChange={(e) => handleTagName(e.target.value)}
          margin='normal'
          error={!!errors.tagName}
          helperText={errors.tagName}
          slotProps={{
            input: {
              sx: labelStyles,
            },
          }}
        />
        <InputLabel sx={colorTextStyles}>
          Color <span style={{ color: 'red' }}>*</span>
        </InputLabel>
        <Select
          value={selectedColor}
          onChange={(e) => setSelectedColor(e.target.value)}
          fullWidth
          displayEmpty
          variant='outlined'
          sx={{ height: '48px', borderRadius: '10px' }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <em style={{ fontSize: '14px', fontStyle: 'normal' }}>
                  Select color
                </em>
              );
            }

            const selectedOption = colorOptions.find(
              (option) => option.value === selected
            );
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    ...chipColorStyles,
                    backgroundColor: selectedOption?.chipColor,
                  }}
                />
                <Typography sx={{ fontSize: '14px' }}>
                  {selectedOption?.label}
                </Typography>
              </Box>
            );
          }}
          slotProps={{
            input: {
              sx: {
                fontSize: '14px',
              },
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: menuItemListStyles,
            },
          }}
        >
          <MenuItem value=''>
            <em style={{ fontSize: '14px', fontStyle: 'normal' }}>
              Select color
            </em>
          </MenuItem>
          {colorOptions.map((color) => (
            <MenuItem key={color.value} value={color.value}>
              <Box
                sx={{
                  ...chipColorStyles,
                  backgroundColor: color.chipColor,
                  marginRight: 1,
                }}
              />
              <Typography sx={{ fontSize: '14px' }}>{color.label}</Typography>
            </MenuItem>
          ))}
        </Select>

        <Box mt={2} mb={2} sx={buttonContainerStyles}>
          <Button onClick={closeDialog} sx={cancelButtonStyles}>
            Cancel{' '}
          </Button>
          <Button
            sx={saveButtonStyles}
            onClick={handleCreateOrUpdateTag}
            disabled={!tagName || !selectedColor}
          >
            {isEditing ? 'Update Tag' : 'Create Tag'}
          </Button>
        </Box>

        <Divider />
        <Typography variant='subtitle1' mt={2} mb={1} fontSize={14}>
          Existing Tags
        </Typography>
        {tags && tags.length > 0 ? (
          <Box sx={{ maxHeight: 250, overflowY: 'auto', pr: 1 }}>
            <List>
              {tags.map((tag, index) => (
                <ListItem key={tag.eventTagId} divider>
                  <Chip
                    label={tag.tagName}
                    sx={{
                      ...chipStyles,
                      backgroundColor: getTagColour(tag.colour),
                    }}
                  />
                  <ListItemText
                    primary={`Used ${tag.usageCount || 0} times`}
                    sx={{
                      marginLeft: 2,
                      '&.MuiListItemText-root .MuiListItemText-primary': {
                        fontSize: '14px',
                      },
                    }}
                  />
                  <IconButton onClick={(e) => handleMenuOpen(e, index)}>
                    <MoreVertIcon fontSize='small' />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            No tags available. Create a new tag to get started.
          </Typography>
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          sx={menuItemStyles}
        >
          <MenuItem onClick={handleEditTag}>Edit</MenuItem>
          <MenuItem onClick={handleDeleteTag}>Delete</MenuItem>
        </Menu>
      </DialogContent>
    </CommonDialog>
  );
}

const labelStyles: SxProps = {
  fontSize: '14px',
  borderRadius: '8px',
  height: '48px',
  '&:-webkit-autofill': {
    boxShadow: '0 0 0 1000px white inset',
    WebkitTextFillColor: 'black',
    borderRadius: '8px',
  },
};

const colorTextStyles: SxProps = {
  fontSize: '14px',
  fontFamily: 400,
  color: colors.black,
  mt: 1,
  mb: 1,
};

const menuItemListStyles: SxProps = {
  maxHeight: 200,
  '& .MuiMenuItem-root': {
    fontSize: '14px',
    height: 32,
    minHeight: 30,
  },
  '&::-webkit-scrollbar': {
    width: '3px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: colors.indigoBlueColor,
    borderRadius: '3px',
  },
};

const saveButtonStyles: SxProps = {
  background: colors.indigoBlueColor,
  textTransform: 'inherit',
  color: colors.white,
  padding: '4px 12px',
  borderRadius: '8px',
  '&:disabled': {
    cursor: 'not-allowed',
    backgroundColor: '#ccc',
    color: '#00000042',
  },
};

const cancelButtonStyles: SxProps = {
  cursor: 'pointer',
  border: `1px solid ${colors.indigoBlueColor}`,
  textTransform: 'inherit',
  color: colors.indigoBlueColor,
  padding: '4px 12px',
  borderRadius: '8px',
};

const buttonContainerStyles: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  gap: 2,
};

const chipStyles: SxProps = {
  color: '#fff',
  fontWeight: 500,
  height: 28,
};

const chipColorStyles: SxProps = { width: 16, height: 16 };
